import React, { useState, useRef, useEffect } from 'react';
import { pageTrackingService } from '../../../../services/pageTracking';
import { Link } from 'react-router';
import { observer } from 'mobx-react';
import { authStore, cartStore, wishlistStore } from '@stores/main';
import { useToast } from '../../../../contexts/ToastContext';
import Product from '@models/product';
import styles from './PortraitCollection.module.scss';
import { useBreadcrumbNavigation } from '@utils/breadcrumbNavigation';
import { formatDiscountPercentage } from '@utils/formatDiscountPercentage';

interface CollectionProduct {
  id?: string;
  name: string;
  price: string;
  gradient: string;
  icon: string;
  imageUrl?: string; // Optional image URL to display instead of icon
  startsFrom?: boolean;
  originalPrice?: string;
  discount?: number;
  tag?: string;
  link?: string; // Product details page URL
  productUrl?: string; // New product details page URL
  isInCartQuantity?: number; // From feed API; also synced via cart store after add
}

interface PortraitCollectionProps {
  title: string;
  subtitle: string;
  products: CollectionProduct[];
  exploreLink?: string;
}

const PortraitCollection: React.FC<PortraitCollectionProps> = observer(({ 
  title, 
  subtitle, 
  products,
  exploreLink,
}) => {
  const { goToLogin } = useBreadcrumbNavigation();
  const { showToast } = useToast();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [imageErrors, setImageErrors] = useState<Set<number>>(new Set());
  const [addingToCart, setAddingToCart] = useState<Set<string>>(new Set());
  const [updatingWishlist, setUpdatingWishlist] = useState<Set<string>>(new Set());

  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    // Delay to ensure DOM is ready
    const timer = setTimeout(() => {
      checkScrollPosition();
    }, 100);

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollPosition);
      window.addEventListener('resize', checkScrollPosition);
      return () => {
        clearTimeout(timer);
        container.removeEventListener('scroll', checkScrollPosition);
        window.removeEventListener('resize', checkScrollPosition);
      };
    }
    return () => clearTimeout(timer);
  }, [products]);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      const firstCard = scrollContainerRef.current.firstElementChild as HTMLElement;
      if (firstCard) {
        const cardWidth = firstCard.offsetWidth;
        const gap = 20;
        const scrollAmount = cardWidth + gap;
        scrollContainerRef.current.scrollBy({
          left: -scrollAmount,
          behavior: 'smooth'
        });
        // Update arrow visibility after scroll
        setTimeout(checkScrollPosition, 300);
      }
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      const firstCard = scrollContainerRef.current.firstElementChild as HTMLElement;
      if (firstCard) {
        const cardWidth = firstCard.offsetWidth;
        const gap = 20;
        const scrollAmount = cardWidth + gap;
        scrollContainerRef.current.scrollBy({
          left: scrollAmount,
          behavior: 'smooth'
        });
        // Update arrow visibility after scroll
        setTimeout(checkScrollPosition, 300);
      }
    }
  };

  const getIconColor = (gradient: string) => {
    if (gradient.includes('#424242') || gradient.includes('#8D6E63') || gradient.includes('#4CAF50') || gradient.includes('#FF9800')) {
      return 'white';
    }
    if (gradient.includes('#E3F2FD')) {
      return '#1976D2';
    }
    if (gradient.includes('#FFF9C4')) {
      return '#F57F17';
    }
    if (gradient.includes('#F8BBD0')) {
      return '#C2185B';
    }
    if (gradient.includes('#E0E0E0')) {
      return '#424242';
    }
    return 'inherit';
  };

  const handleImageError = (index: number) => {
    setImageErrors(prev => new Set(prev).add(index));
  };

  // Creative fallback image when product image is missing or fails to load (same as product details page)
  const getFallbackImageUrl = (): string => {
    return `${process.env.PUBLIC_URL || ''}/images/product-placeholder.svg`;
  };

  const handleAddToCart = async (e: React.MouseEvent, product: CollectionProduct) => {
    e.preventDefault();
    e.stopPropagation();

    if (!product.id) {
      showToast('Product ID is missing. Please try again.', 'error', 3000);
      return;
    }

    if (!authStore.isAuthenticated) {
      goToLogin();
      return;
    }

    setAddingToCart(prev => new Set(prev).add(product.id!));
    
    try {
      const res = await cartStore.addItemToCart(product.id);
      if (!res) {
        return;
      }
      pageTrackingService.trackProductAddToCart(String(product.id), product.name || '', 'home_collection');
    } catch (error) {
      console.error("Error adding to cart:", error);
    } finally {
      setAddingToCart(prev => {
        const newSet = new Set(prev);
        newSet.delete(product.id!);
        return newSet;
      });
    }
  };

  const handleWishlistToggle = async (e: React.MouseEvent, product: CollectionProduct) => {
    e.preventDefault();
    e.stopPropagation();

    if (!product.id) {
      showToast('Product ID is missing. Please try again.', 'error', 3000);
      return;
    }

    if (!authStore.isAuthenticated) {
      goToLogin();
      return;
    }

    setUpdatingWishlist(prev => new Set(prev).add(product.id!));

    try {
      const isInWishlist = wishlistStore.wishlist.some((item) => item.id === product.id);
      
      if (isInWishlist) {
        wishlistStore.removeProductFromWishlist(product as unknown as Product);
        showToast(`Removed ${product.name} from wishlist`, 'info', 2500);
      } else {
        await wishlistStore.addProductToWishlist(product as unknown as Product);
      }
    } catch (error) {
      console.error("Error updating wishlist:", error);
    } finally {
      setUpdatingWishlist(prev => {
        const newSet = new Set(prev);
        newSet.delete(product.id!);
        return newSet;
      });
    }
  };

  const isInWishlist = (productId?: string): boolean => {
    if (!productId) return false;
    return wishlistStore.wishlist.some((item) => item.id === productId);
  };

  // In cart: from feed is_in_cart_quantity or from cart store (after add)
  const getCartQuantity = (productId?: string, feedQuantity?: number): number => {
    if (!productId) return 0;
    const cartItem = cartStore.items.find((item) => item.id === productId);
    if (cartItem) return Math.max(0, cartItem.isInCartQuantity ?? cartItem.quantity ?? 1);
    return Math.max(0, feedQuantity ?? 0);
  };
  const isInCart = (product?: CollectionProduct): boolean => getCartQuantity(product?.id, product?.isInCartQuantity) > 0;

  return (
    <section className={styles.contentSection}>
      <div className={styles.sectionHeader}>
        <div className={styles.titleRow}>
          <h2 className={styles.sectionTitle}>{title}</h2>
          {exploreLink != null && exploreLink !== '' && exploreLink !== '#' && (
            <Link to={exploreLink} className={styles.exploreAll}>
              Explore All <i className="fas fa-arrow-right"></i>
            </Link>
          )}
        </div>
        <p className={styles.sectionSubtitle}>{subtitle}</p>
      </div>
      <div className={styles.sliderContainer}>
        {showLeftArrow && (
          <button className={`${styles.scrollButton} ${styles.left}`} onClick={scrollLeft} aria-label="Scroll left">
            <i className="fas fa-chevron-left"></i>
          </button>
        )}
        <div className={styles.productGrid} ref={scrollContainerRef}>
          {products.map((product, index) => (
            <div key={index} className={styles.productCard}>
              <Link to={product.productUrl || product.link || "#"} className={styles.productLink}>
                <div
                  className={`${styles.productImage} ${product.imageUrl ? styles.hasImage : ''}`}
                  style={{ 
                    background: product.imageUrl ? 'transparent' : product.gradient 
                  }}
                >
                  {product.discount != null && product.discount > 0 && (
                    <div className={styles.discountBadge}>
                      {formatDiscountPercentage(product.discount)}% OFF
                    </div>
                  )}
                  {product.tag && (
                    <div 
                      className={styles.productTag} 
                      data-tag={product.tag}
                    >
                      {product.tag}
                    </div>
                  )}
                  <button
                    className={`${styles.wishlistBtn} ${isInWishlist(product.id) ? styles.wishlistBtnActive : ''}`}
                    onClick={(e) => handleWishlistToggle(e, product)}
                    disabled={updatingWishlist.has(product.id || '')}
                    aria-label={isInWishlist(product.id) ? "Remove from wishlist" : "Add to wishlist"}
                  >
                    {updatingWishlist.has(product.id || '') ? (
                      <i className="fas fa-spinner fa-spin"></i>
                    ) : (
                      <i className={`fas ${isInWishlist(product.id) ? 'fa-heart' : 'fa-heart'}`}></i>
                    )}
                  </button>
                  {product.imageUrl ? (
                    !imageErrors.has(index) ? (
                      <img 
                        src={product.imageUrl} 
                        alt={product.name}
                        className={styles.productImageContent}
                        onError={() => handleImageError(index)}
                        onLoad={(e) => {
                          // Check if image actually loaded (some servers return 200 with error image)
                          const img = e.target as HTMLImageElement;
                          if (img.naturalWidth === 0 || img.naturalHeight === 0) {
                            handleImageError(index);
                          }
                        }}
                      />
                    ) : (
                      <img 
                        src={getFallbackImageUrl()} 
                        alt={product.name}
                        className={styles.productImageContent}
                      />
                    )
                  ) : (
                    <div style={{
                      color: getIconColor(product.gradient),
                      fontSize: '48px'
                    }}>
                      <i className={product.icon}></i>
                    </div>
                  )}
                </div>
                <div className={styles.productInfo}>
                  <div className={styles.productName} title={product.name}>{product.name}</div>
                  {product.startsFrom && (
                    <span className={styles.startsFromLabel}>Starts From</span>
                  )}
                  <div className={styles.productPriceContainer}>
                    {product.originalPrice && (
                      <span className={styles.originalPrice}>{product.originalPrice}</span>
                    )}
                    <span className={styles.productPrice}>{product.price}</span>
                    <button
                      className={`${styles.addToCartIcon} ${isInCart(product) ? styles.addToCartIconInCart : ''}`}
                      onClick={(e) => handleAddToCart(e, product)}
                      disabled={addingToCart.has(product.id || '') || !product.id}
                      aria-label={isInCart(product) ? "In cart" : "Add to Cart"}
                      title={isInCart(product) ? `In cart (${getCartQuantity(product.id, product.isInCartQuantity)})` : 'Add to Cart'}
                    >
                      {addingToCart.has(product.id || '') ? (
                        <i className="fas fa-spinner fa-spin" style={{ fontSize: '16px' }}></i>
                      ) : isInCart(product) ? (
                        <>
                          <i className="fas fa-shopping-cart" style={{ fontSize: '18px' }}></i>
                          {getCartQuantity(product.id, product.isInCartQuantity) > 1 && (
                            <span className={styles.cartQuantityBadge}>{getCartQuantity(product.id, product.isInCartQuantity)}</span>
                          )}
                        </>
                      ) : (
                        <img
                          src={process.env.PUBLIC_URL + "/images/icons/add-to-cart.svg"}
                          alt="Add to Cart"
                          width={24}
                          height={24}
                        />
                      )}
                    </button>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
        {showRightArrow && (
          <button className={`${styles.scrollButton} ${styles.right}`} onClick={scrollRight} aria-label="Scroll right">
            <i className="fas fa-chevron-right"></i>
          </button>
        )}
      </div>
    </section>
  );
});

export default PortraitCollection;

